/**
 * OverlayGenerator - Gera imagens com overlay de branding
 * 
 * Usa Canvas API para adicionar barra inferior com:
 * - Dados da captura (espécie, peso)
 * - Localização
 * - Check-in (pousada)
 * - Branding "via @nossapesca"
 */

export interface OverlayData {
  // Dados da captura
  species?: string;
  weight?: number; // em kg
  size?: number; // em cm
  
  // Localização
  location?: string;
  state?: string;
  
  // Check-in (pousada/guia)
  checkIn?: {
    name: string;
    instagram?: string;
  };
  
  // Configurações
  showBranding?: boolean; // default: true (grátis com marca)
  brandingText?: string; // default: "via @nossapesca"
}

export interface OverlayConfig {
  barHeight: number;
  backgroundColor: string;
  textColor: string;
  accentColor: string;
  fontSize: number;
  padding: number;
  fontFamily: string;
}

const DEFAULT_CONFIG: OverlayConfig = {
  barHeight: 120,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  textColor: '#FFFFFF',
  accentColor: '#F59E0B', // amber-500
  fontSize: 16,
  padding: 16,
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

/**
 * Gera uma imagem com overlay de branding
 */
export async function generateOverlay(
  imageUrl: string,
  data: OverlayData,
  config: Partial<OverlayConfig> = {}
): Promise<Blob> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  
  // Carrega a imagem original
  const img = await loadImage(imageUrl);
  
  // Cria canvas com dimensões da imagem + barra
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  
  canvas.width = img.width;
  canvas.height = img.height + cfg.barHeight;
  
  // Desenha imagem original
  ctx.drawImage(img, 0, 0);
  
  // Desenha barra inferior
  ctx.fillStyle = cfg.backgroundColor;
  ctx.fillRect(0, img.height, canvas.width, cfg.barHeight);
  
  // Configura texto
  ctx.fillStyle = cfg.textColor;
  ctx.font = `bold ${cfg.fontSize}px ${cfg.fontFamily}`;
  ctx.textBaseline = 'middle';
  
  const lineHeight = cfg.fontSize + 8;
  let y = img.height + cfg.padding + lineHeight / 2;
  const x = cfg.padding;
  
  // Linha 1: Espécie e peso
  if (data.species || data.weight) {
    const fishEmoji = '🐟';
    let text = fishEmoji + ' ';
    
    if (data.weight) {
      text += `${data.weight}kg`;
    }
    if (data.species) {
      text += data.weight ? ` | ${data.species}` : data.species;
    }
    
    ctx.fillStyle = cfg.accentColor;
    ctx.fillText(text, x, y);
    y += lineHeight;
  }
  
  // Linha 2: Localização
  if (data.location || data.state) {
    ctx.fillStyle = cfg.textColor;
    const locationText = `📍 ${data.location || ''}${data.state ? ` - ${data.state}` : ''}`;
    ctx.fillText(locationText, x, y);
    y += lineHeight;
  }
  
  // Linha 3: Check-in
  if (data.checkIn) {
    ctx.fillStyle = cfg.textColor;
    const checkInText = `🏨 ${data.checkIn.name}`;
    ctx.fillText(checkInText, x, y);
    y += lineHeight;
  }
  
  // Branding (canto inferior direito)
  if (data.showBranding !== false) {
    const brandText = data.brandingText || 'via @nossapesca';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = `${cfg.fontSize - 2}px ${cfg.fontFamily}`;
    const textWidth = ctx.measureText(brandText).width;
    ctx.fillText(
      brandText,
      canvas.width - textWidth - cfg.padding,
      img.height + cfg.barHeight - cfg.padding
    );
  }
  
  // Converte para Blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Falha ao gerar imagem'));
        }
      },
      'image/jpeg',
      0.92
    );
  });
}

/**
 * Carrega uma imagem de uma URL
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Gera preview do overlay (retorna data URL para exibição)
 */
export async function generateOverlayPreview(
  imageUrl: string,
  data: OverlayData,
  config?: Partial<OverlayConfig>
): Promise<string> {
  const blob = await generateOverlay(imageUrl, data, config);
  return URL.createObjectURL(blob);
}

export default {
  generateOverlay,
  generateOverlayPreview,
};
